<!--
 * @Author: Christion.Young
 * @Date: 2025-07-30 10:00:00
 * @LastEditors: Christion.Young
 * @LastEditTime: 2025-08-13 14:30:00
 * @Description: 商户详情页面 - 使用真实API数据展示完整商户信息
-->
<template>
  <div class="merchant-detail-page">
    <div class="merchant-page-container">
      <!-- 加载状态 -->
      <div v-if="loading" class="loading-wrapper">
        <div class="skeleton-container">
          <div class="skeleton-line" v-for="n in 8" :key="n"></div>
        </div>
      </div>

      <!-- 商户详情 -->
      <div v-else-if="merchantInfo" class="merchant-detail">
        <!-- 返回按钮 -->
        <!-- <div class="back-button">
          <button class="back-btn" @click="goBack">
            <i class="el-icon-back"></i>
            返回列表
          </button>
        </div> -->

        <!-- 商户基本信息卡片 -->
        <div class="merchant-info-card">
          <div class="merchant-info-layout">
            <!-- 左侧：商户基本信息 -->
            <div class="merchant-info-left">
              <div class="merchant-header">
                <div class="merchant-logo">
                  <img
                    :src="getImageUrl(merchantInfo.merchantCover)"
                    :alt="merchantInfo.name"
                  />
                </div>
                <div class="merchant-basic">
                  <h1 class="merchant-name">{{ merchantInfo.name }}</h1>
                  <div class="merchant-category">
                    {{ getMerchantTypeText(merchantInfo.type) }}
                  </div>
                  <div class="merchant-area">{{ merchantInfo.area }}</div>
                </div>
                <div class="merchant-stats">
                  <div class="stat-item">
                    <div class="stat-value">{{ totalSales }}单</div>
                    <div class="stat-label">成交订单</div>
                  </div>
                  <div class="stat-item">
                    <div class="stat-value">{{ serviceList.length }}个</div>
                    <div class="stat-label">服务项目</div>
                  </div>
                  <div class="stat-item">
                    <div class="stat-value">{{ staffList.length }}人</div>
                    <div class="stat-label">服务人员</div>
                  </div>
                </div>
              </div>

              <div class="merchant-contact">
                <div class="contact-item">
                  <i class="el-icon-location"></i>
                  <span>{{ merchantInfo.address }}</span>
                </div>
                <div class="contact-item">
                  <i class="el-icon-phone"></i>
                  <span>{{ formatPhonePrivacy(merchantInfo.phone) }}</span>
                </div>
                <div class="contact-item">
                  <i class="el-icon-time"></i>
                  <span
                    >营业时间：{{ merchantInfo.businessStartTime }} -
                    {{ merchantInfo.businessEndTime }}</span
                  >
                </div>
              </div>

              <div class="merchant-description">
                <h3>商户介绍</h3>
                <p>{{ merchantInfo.remark }}</p>
              </div>
            </div>

            <!-- 右侧：图片展示区域 -->
            <div class="merchant-images-right">
              <h3 class="images-title">店铺图片</h3>
              <div class="images-container">
                <div
                  v-for="(image, index) in shopImages"
                  :key="index"
                  class="image-item"
                >
                  <img
                    :src="image"
                    :alt="`${merchantInfo.name} - 店铺图片${index + 1}`"
                    @click="previewImage(image)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Tab导航和内容 -->
        <div class="merchant-tabs">
          <div class="tab-navigation">
            <button
              v-for="(tab, index) in tabList"
              :key="index"
              class="tab-nav-item"
              :class="{ active: currentTab === index }"
              @click="currentTab = index"
            >
              {{ tab.name }}
            </button>
            <!-- 客服按钮 -->
            <!-- <button
              v-if="staffList.length > 0"
              class="customer-service-btn"
              @click="contactFirstStaff"
            >
              <i class="el-icon-chat-dot-round"></i>
              {{ $store.getters["user/isLoggedIn"] ? "客服" : "登录聊天" }}
            </button> -->
          </div>

          <div class="tab-content">
            <!-- 店铺服务 -->
            <div v-if="currentTab === 0" class="tab-panel services-panel">
              <div v-if="serviceList.length > 0" class="services-grid">
                <div
                  v-for="service in serviceList"
                  :key="service.id"
                  class="service-card"
                  @click="viewService(service.id)"
                >
                  <img
                    :src="getImageUrl(service.avatarImg)"
                    :alt="service.name"
                    class="service-image"
                  />
                  <div class="service-info">
                    <h4 class="service-name">{{ service.name }}</h4>
                    <p class="service-desc">{{ service.serviceDesc }}</p>
                    <div class="service-price">¥{{ service.servicePrice }}</div>
                    <div class="service-meta">
                      <span class="service-sales"
                        >已售{{ service.salesNumber }}单</span
                      >
                      <span
                        class="service-rating"
                        v-if="service.evaluate && service.evaluate !== '0'"
                      >
                        {{ service.evaluate }}分
                      </span>
                    </div>
                    <div class="service-tags">
                      <span class="service-tag">{{
                        getServiceWayText(service.serviceWay)
                      }}</span>
                      <span class="service-tag">{{
                        getOrderTypeText(service.orderType)
                      }}</span>
                      <span
                        class="service-status-tag"
                        :class="service.isOnline === '1' ? 'online' : 'offline'"
                      >
                        {{ service.isOnline === "1" ? "在线" : "打烊" }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="no-services">
                <p>暂无服务信息</p>
              </div>
            </div>

            <!-- 店铺员工 -->
            <div v-if="currentTab === 1" class="tab-panel staff-panel">
              <div v-if="staffList.length > 0" class="staff-section">
                <!-- 员工选择列表 -->
                <div class="staff-list">
                  <div
                    v-for="staff in staffList"
                    :key="staff.userId"
                    class="staff-item"
                    :class="{
                      selected: selectedStaff?.userId === staff.userId,
                    }"
                    @click="selectStaff(staff)"
                  >
                    <img
                      :src="getImageUrl(staff.avatar)"
                      :alt="staff.nickname"
                      class="staff-avatar"
                    />
                    <div class="staff-basic">
                      <div class="staff-name">{{ staff.nickname }}</div>
                      <div class="staff-tags">
                        <span class="staff-type-tag">{{
                          getStaffTypeText(staff.type)
                        }}</span>
                        <span class="staff-sex-tag">{{
                          getSexText(staff.sex)
                        }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 选中员工详情 -->
                <div v-if="selectedStaff" class="staff-detail">
                  <div class="staff-detail-header">
                    <img
                      :src="getImageUrl(selectedStaff.avatar)"
                      :alt="selectedStaff.nickname"
                      class="staff-detail-avatar"
                    />
                    <div class="staff-detail-info">
                      <h3>{{ selectedStaff.nickname }}</h3>
                      <div class="staff-detail-tags">
                        <span class="tag">{{
                          getStaffTypeText(selectedStaff.type)
                        }}</span>
                        <span class="tag">{{
                          getSexText(selectedStaff.sex)
                        }}</span>
                      </div>
                      <button
                        class="chat-btn"
                        @click="chatWithStaff(selectedStaff)"
                      >
                        <i class="el-icon-chat-dot-round"></i>
                        {{
                          $store.getters["user/isLoggedIn"]
                            ? "聊天"
                            : "登录聊天"
                        }}
                      </button>
                    </div>
                  </div>

                  <div class="staff-description">
                    <h4>个人简介</h4>
                    <p>{{ selectedStaff.staffDesc }}</p>
                  </div>

                  <div v-if="staffImages.length > 0" class="staff-images">
                    <h4>相关图片</h4>
                    <div class="staff-image-grid">
                      <img
                        v-for="(img, index) in staffImages"
                        :key="index"
                        :src="img"
                        :alt="`${selectedStaff.nickname} - 图片${index + 1}`"
                        @click="previewImage(img)"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="no-staff">
                <p>暂无员工信息</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 错误状态 -->
      <div v-else class="error-state">
        <i class="el-icon-warning"></i>
        <p>商户信息不存在</p>
        <button class="error-btn" @click="goBack">返回列表</button>
      </div>
    </div>
  </div>
</template>

<script>
// 导入图片处理工具
import { getImageUrl } from "@/utils/imageUtils";
// 导入API
import { noPermissionRequired } from "@/api/index";
import API from "@/api/api";
import { formatPhonePrivacy } from "@/utils/common";

export default {
  name: "MerchantDetail",
  data() {
    return {
      loading: true,
      currentTab: 0, // 0-店铺服务, 1-店铺员工

      // 商户信息
      merchantInfo: null,
      serviceList: [],
      staffList: [],
      totalSales: 0,
      selectedStaff: null,

      // Tab配置
      tabList: [
        { value: "0", name: "店铺服务", color: "#2485D5" },
        { value: "1", name: "店铺员工", color: "#F5BD00" },
      ],

      // 字典配置
      merchantTypeOptions: [
        { value: "0", label: "法人或个体工商户" },
        { value: "1", label: "自然人" },
      ],
      sexOptions: [
        { value: "1", label: "男" },
        { value: "2", label: "女" },
      ],
      staffTypeOptions: [
        { value: "1", label: "高级" },
        { value: "2", label: "中级" },
        { value: "3", label: "初级" },
        { value: "4", label: "全职" },
        { value: "5", label: "兼职" },
      ],
      serviceWayOptions: [
        { value: 1, label: "到店服务" },
        { value: 2, label: "上门服务" },
        { value: 3, label: "线上服务" },
      ],
      orderTypeOptions: [
        { value: "1", label: "单一接单" },
        { value: "2", label: "同时多单" },
      ],
    };
  },

  computed: {
    // 店铺图片列表
    shopImages() {
      if (!this.merchantInfo || !this.merchantInfo.shopPictureImgList) {
        return [require("@/assets/logo.png")];
      }
      const images = this.merchantInfo.shopPictureImgList
        .split(",")
        .filter((img) => img.trim());
      return images.length > 0
        ? images.map((img) => getImageUrl(img.trim()))
        : [require("@/assets/logo.png")];
    },

    // 员工图片列表
    staffImages() {
      if (!this.selectedStaff || !this.selectedStaff.imgList) return [];
      return this.selectedStaff.imgList
        .split(",")
        .filter((img) => img.trim())
        .map((img) => getImageUrl(img.trim()));
    },
  },

  created() {
    this.loadMerchantDetail();
  },

  methods: {
    /**
     * 手机号隐私处理
     */
    formatPhonePrivacy,

    /**
     * 加载商户详情 - 使用真实API
     */
    async loadMerchantDetail() {
      this.loading = true;
      try {
        let merchantId = this.$route.params.id;
        if (!merchantId || merchantId === "0" || merchantId === "undefined") {
          merchantId = "120"; // 默认ID
        }

        // 并行加载商户信息、服务列表、员工信息
        const [merchantResponse, serviceResponse, staffResponse] =
          await Promise.allSettled([
            noPermissionRequired.getMerchantID(merchantId),
            noPermissionRequired.getServiceListNoToken({
              merchantId: merchantId,
              current: 1,
              size: 99,
            }),
            noPermissionRequired.getMerchantStaffPage({
              merchantId: merchantId,
              current: 1,
              size: 99,
            }),
          ]);

        // 处理商户信息
        if (
          merchantResponse.status === "fulfilled" &&
          merchantResponse.value?.data
        ) {
          this.merchantInfo = merchantResponse.value.data;
        } else {
          console.warn("加载商户信息失败:", merchantResponse.reason);
          this.merchantInfo = null;
        }

        // 处理服务列表
        if (
          serviceResponse.status === "fulfilled" &&
          serviceResponse.value?.data?.records
        ) {
          this.serviceList = serviceResponse.value.data.records;
        } else {
          console.warn("加载服务列表失败:", serviceResponse.reason);
          this.serviceList = [];
        }

        // 处理员工信息
        if (
          staffResponse.status === "fulfilled" &&
          staffResponse.value?.data?.records
        ) {
          this.staffList = staffResponse.value.data.records.map((staff) => ({
            ...staff,
            isOptFor: false,
          }));

          // 默认选择第一个员工
          if (this.staffList.length > 0) {
            this.selectedStaff = { ...this.staffList[0], isOptFor: true };
            this.staffList[0].isOptFor = true;
          }
        } else {
          console.warn("加载员工信息失败:", staffResponse.reason);
          this.staffList = [];
        }

        // 成交订单数：调用平台佣金分页接口统计
        try {
          const commissionRes = await API.user.platformCommissionPage({
            current: 1,
            size: 1,
            merchantId: merchantId,
          });
          if (commissionRes && commissionRes.code === 0 && commissionRes.data) {
            this.totalSales = commissionRes.data.total || 0;
          } else {
            this.totalSales = 0;
          }
        } catch (e) {
          this.totalSales = 0;
        }

        // 如果没有商户信息，表示加载失败
        if (!this.merchantInfo) {
          throw new Error("商户信息加载失败");
        }
      } catch (error) {
        console.error("加载商户详情失败:", error);
        this.$msg.error("加载商户详情失败，请稍后重试");
        this.merchantInfo = null;
      } finally {
        this.loading = false;
      }
    },

    /**
     * 返回列表
     */
    goBack() {
      this.$router.go(-1);
    },

    /**
     * 获取图片URL
     */
    getImageUrl(imageName) {
      return getImageUrl(imageName);
    },

    /**
     * 查看服务详情
     */
    viewService(serviceId) {
      if (!serviceId) {
        this.$msg.warning("服务ID不能为空");
        return;
      }

      this.$router
        .push({
          name: "serviceDetail",
          params: { id: serviceId },
        })
        .catch((err) => {
          console.error("跳转到服务详情失败:", err);
          this.$msg.error("跳转失败，请稍后重试");
        });
    },

    /**
     * 选择员工
     */
    selectStaff(staff) {
      // 重置所有员工选择状态
      this.staffList.forEach((s) => {
        s.isOptFor = false;
      });

      // 设置选中员工
      staff.isOptFor = true;
      this.selectedStaff = { ...staff };
    },

    /**
     * 与员工聊天
     */
    async chatWithStaff(staff) {
      // 检查登录状态
      if (!this.$store.getters["user/isLoggedIn"]) {
        this.$msg.warning("请先登录后再进行聊天");
        this.$bus.emit("show-login-dialog");
        return;
      }

      // 显示加载提示
      const closeLoading = this.$msg.loading("正在加载聊天记录...");

      try {
        // 获取聊天历史记录 - 使用正确的字段名 userId
        const chatHistory = await this.loadChatHistory(staff.userId);

        // 关闭加载提示
        closeLoading();

        // 使用选中的员工ID打开聊天对话框
        this.$bus.emit("show-chat-dialog", {
          employeeId: staff.userId,
          employeeName: staff.nickname || staff.staffName,
          chatType: "employee",
          chatHistory: chatHistory,
        });
      } catch (error) {
        // 关闭加载提示
        closeLoading();

        console.error("获取聊天历史失败:", error);

        // 即使失败也要打开聊天框
        this.$bus.emit("show-chat-dialog", {
          employeeId: staff.userId,
          employeeName: staff.nickname || staff.staffName,
          chatType: "employee",
          chatHistory: [],
        });

        this.$msg.warning("获取聊天历史记录失败，但聊天功能正常");
      }
    },

    /**
     * 联系第一个员工（客服功能）
     */
    async contactFirstStaff() {
      // 检查登录状态
      if (!this.$store.getters["user/isLoggedIn"]) {
        this.$msg.warning("请先登录后再进行聊天");
        this.$bus.emit("show-login-dialog");
        return;
      }

      if (this.staffList.length > 0) {
        const firstEmployee = this.staffList[0];

        // 显示加载提示
        const closeLoading = this.$msg.loading("正在加载聊天记录...");

        try {
          // 获取聊天历史记录 - 使用正确的字段名 userId
          const chatHistory = await this.loadChatHistory(firstEmployee.userId);

          // 关闭加载提示
          closeLoading();

          this.$bus.emit("show-chat-dialog", {
            employeeId: firstEmployee.userId,
            employeeName: firstEmployee.nickname || firstEmployee.staffName,
            chatType: "service",
            chatHistory: chatHistory,
          });
        } catch (error) {
          // 关闭加载提示
          closeLoading();

          console.error("获取聊天历史失败:", error);

          // 即使失败也要打开聊天框
          this.$bus.emit("show-chat-dialog", {
            employeeId: firstEmployee.userId,
            employeeName: firstEmployee.nickname || firstEmployee.staffName,
            chatType: "service",
            chatHistory: [],
          });

          this.$msg.warning("获取聊天历史记录失败，但聊天功能正常");
        }
      } else {
        this.$msg.warning("暂无可用的客服人员");
      }
    },

    /**
     * 获取聊天历史记录
     * @param {string} toUserId - 聊天对象用户ID
     * @returns {Promise<Array>} 聊天历史记录数组
     */
    async loadChatHistory(toUserId) {
      try {
        // 获取当前用户ID
        const userInfo = this.$store.getters["user/userInfo"];
        const currentUserId = userInfo?.id || userInfo?.userId;

        if (!currentUserId) {
          console.warn("无法获取当前用户ID，跳过获取聊天历史");
          return [];
        }

        console.log("正在获取聊天历史记录:", {
          fromUserId: currentUserId,
          toUserId,
        });

        // 调用聊天历史记录接口
        const response = await API.message.messageChatHistory(
          currentUserId,
          toUserId
        );

        if (response && response.code === 0 && response.data) {
          console.log(
            "聊天历史记录获取成功，共",
            response.data.length,
            "条记录"
          );
          return this.formatChatHistory(response.data);
        } else {
          console.warn("获取聊天历史失败:", response?.msg || "未知错误");
          return [];
        }
      } catch (error) {
        console.error("获取聊天历史记录失败:", error);
        return [];
      }
    },

    /**
     * 格式化聊天历史记录数据
     * @param {Array} historyList - 原始历史记录数据
     * @returns {Array} 格式化后的聊天记录
     */
    formatChatHistory(historyList) {
      if (!Array.isArray(historyList)) {
        return [];
      }

      return historyList.map((message) => ({
        id: message.id,
        fromUserId: message.fromUserId,
        fromUserName: message.fromUserName,
        toUserId: message.toUserId,
        toUserName: message.toUserName,
        content: message.content,
        msgType: message.msgType || "3",
        orderId: message.orderId,
        page: message.page,
        readStatus: message.readStatus,
        createTime: message.createTime,
        updateTime: message.updateTime,
        isRevoked: message.isRevoked || false,
        extraData: message.extraData || {},
        // 添加实际API返回的字段
        createId: message.createId,
        updateId: message.updateId,
        delFlag: message.delFlag,
        tenantId: message.tenantId,
        timeSort: message.timeSort,
      }));
    },

    /**
     * 预览图片
     */
    previewImage(image) {
      console.log("预览图片:", image);
    },

    /**
     * 格式化距离
     */
    formatDistance(distance) {
      const dist = parseFloat(distance) || 0;
      if (dist < 1000) {
        return `${dist}m`;
      } else {
        return `${(dist / 1000).toFixed(1)}km`;
      }
    },

    /**
     * 获取商户类型文本
     */
    getMerchantTypeText(value) {
      const option = this.merchantTypeOptions.find(
        (opt) => opt.value === value
      );
      return option ? option.label : "未知";
    },

    /**
     * 获取性别文本
     */
    getSexText(value) {
      const option = this.sexOptions.find((opt) => opt.value === value);
      return option ? option.label : "未知";
    },

    /**
     * 获取员工类型文本
     */
    getStaffTypeText(value) {
      const option = this.staffTypeOptions.find((opt) => opt.value === value);
      return option ? option.label : "未知";
    },

    /**
     * 获取服务方式文本
     */
    getServiceWayText(value) {
      const option = this.serviceWayOptions.find(
        (opt) => opt.value === parseInt(value)
      );
      return option ? option.label : "未知";
    },

    /**
     * 获取接单类型文本
     */
    getOrderTypeText(value) {
      const option = this.orderTypeOptions.find((opt) => opt.value === value);
      return option ? option.label : "未知";
    },
  },
};
</script>

<style lang="scss" scoped>
// 红色主题色彩定义
$primary-red: #e74c3c;
$primary-red-dark: #c0392b;
$primary-red-light: #f8d7da;

.merchant-detail-page {
  min-height: 100vh;
  background: #f8f9fa;
  padding: 16px 0;

  // 防止外部样式污染
  * {
    box-sizing: border-box;
  }

  // 重置可能被污染的样式
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 0;
    font-weight: normal;
  }

  p {
    margin: 0;
  }

  ul,
  ol {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  button {
    border: none;
    background: none;
    cursor: pointer;
    font-family: inherit;
  }

  img {
    max-width: 100%;
    height: auto;
    display: block;
  }
}

.merchant-page-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px;
}

/* 加载状态 */
.loading-wrapper {
  .skeleton-container {
    background: white;
    border-radius: 8px;
    padding: 24px;
    margin-bottom: 24px;

    .skeleton-line {
      height: 16px;
      background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
      background-size: 200% 100%;
      animation: loading 1.5s infinite;
      border-radius: 4px;
      margin-bottom: 12px;

      &:nth-child(1) {
        width: 60%;
      }
      &:nth-child(2) {
        width: 100%;
      }
      &:nth-child(3) {
        width: 80%;
      }
      &:nth-child(4) {
        width: 90%;
      }
      &:nth-child(5) {
        width: 70%;
      }
      &:nth-child(6) {
        width: 85%;
      }
      &:nth-child(7) {
        width: 95%;
      }
      &:nth-child(8) {
        width: 75%;
      }
    }
  }
}

/* 返回按钮 */
.back-button {
  margin-bottom: 16px;

  .back-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background: white;
    border: 1px solid #ddd;
    border-radius: 6px;
    color: #333;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 13px;

    &:hover {
      border-color: $primary-red;
      color: $primary-red;
    }
  }
}

.merchant-detail {
  > div {
    background: white;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    margin-bottom: 20px;
    padding: 24px;
  }
}

/* 商户信息卡片 */
.merchant-info-card {
  .merchant-info-layout {
    display: flex;
    gap: 32px;
    min-height: 400px;

    .merchant-info-left {
      flex: 1; // 占据一半空间
      min-width: 0; // 防止flex子项溢出
    }

    .merchant-images-right {
      flex: 1; // 占据一半空间
      min-width: 0; // 防止flex子项溢出

      .images-title {
        font-size: 16px;
        font-weight: 600;
        color: #2c3e50;
        margin-bottom: 16px;
        padding-bottom: 8px;
        border-bottom: 2px solid $primary-red;
      }

      .images-container {
        height: 360px;
        overflow-y: auto;
        border: 1px solid #e8e8e8;
        border-radius: 8px;
        padding: 12px;
        background: #fafafa;

        &::-webkit-scrollbar {
          width: 6px;
        }

        &::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }

        &::-webkit-scrollbar-thumb {
          background: $primary-red;
          border-radius: 3px;

          &:hover {
            background: $primary-red-dark;
          }
        }

        .image-item {
          margin-bottom: 12px;

          &:last-child {
            margin-bottom: 0;
          }

          img {
            width: 100%;
            height: auto;
            border-radius: 6px;
            cursor: pointer;
            transition: all 0.3s ease;
            border: 1px solid #e0e0e0;

            &:hover {
              border-color: $primary-red;
              box-shadow: 0 4px 12px rgba(231, 76, 60, 0.2);
            }
          }
        }
      }
    }
  }

  .merchant-header {
    display: flex;
    gap: 16px;
    margin-bottom: 20px;
    align-items: flex-start;

    .merchant-logo {
      width: 80px;
      height: 80px;
      border-radius: 8px;
      overflow: hidden;
      flex-shrink: 0;
      border: 1px solid #f0f0f0;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    .merchant-basic {
      flex: 1;

      .merchant-name {
        font-size: 22px;
        font-weight: 600;
        color: #2c3e50;
        margin-bottom: 8px;
        line-height: 1.2;
      }

      .merchant-category {
        font-size: 14px;
        color: #666;
        margin-bottom: 4px;
        padding: 2px 8px;
        background: #f8f9fa;
        border-radius: 12px;
        display: inline-block;
      }

      .merchant-area {
        font-size: 13px;
        color: #999;
        margin-top: 8px;
      }
    }

    .merchant-stats {
      display: flex;
      gap: 20px;

      .stat-item {
        text-align: center;

        .stat-value {
          font-size: 18px;
          font-weight: 600;
          color: $primary-red;
          line-height: 1;
        }

        .stat-label {
          font-size: 12px;
          color: #999;
          margin-top: 4px;
        }
      }
    }
  }

  .merchant-contact {
    margin-bottom: 20px;

    .contact-item {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 8px;
      font-size: 14px;

      i {
        width: 16px;
        color: $primary-red;
        flex-shrink: 0;
      }

      span {
        flex: 1;
        color: #555;
      }

      .contact-btn {
        padding: 6px 16px;
        background: $primary-red;
        color: white;
        border-radius: 4px;
        font-size: 12px;

        &:hover {
          background: $primary-red-dark;
        }
      }
    }
  }

  .merchant-description {
    h3 {
      font-size: 16px;
      font-weight: 600;
      color: #2c3e50;
      margin-bottom: 12px;
    }

    p {
      font-size: 14px;
      color: #666;
      line-height: 1.6;
    }
  }
}

/* Tab导航 */
.merchant-tabs {
  .tab-navigation {
    display: flex;
    border-bottom: 2px solid #e4e7ed;
    background: white;
    border-radius: 8px 8px 0 0;
    position: relative;

    .tab-nav-item {
      flex: 1;
      padding: 16px 20px;
      border: none;
      background: none;
      color: #606266;
      cursor: pointer;
      font-size: 15px;
      font-weight: 500;
      position: relative;

      &:hover {
        color: $primary-red;
      }

      &.active {
        color: $primary-red;
        font-weight: 600;

        &::after {
          content: "";
          position: absolute;
          bottom: -2px;
          left: 0;
          right: 0;
          height: 3px;
          background: $primary-red;
          border-radius: 2px 2px 0 0;
        }
      }
    }

    .customer-service-btn {
      position: absolute;
      right: 20px;
      top: 50%;
      transform: translateY(-50%);
      padding: 8px 16px;
      background: $primary-red;
      color: white;
      border-radius: 6px;
      font-size: 13px;
      display: flex;
      align-items: center;
      gap: 6px;

      &:hover {
        background: $primary-red-dark;
      }
    }
  }

  .tab-content {
    background: white;
    border-radius: 0 0 8px 8px;
    min-height: 400px;

    .tab-panel {
      padding: 24px 0;
    }
  }
}

/* 服务列表 */
.services-panel {
  .services-grid {
    /* Grid 布局 - 确保3列显示 */
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
    align-items: start;
    width: 100%;

    .service-card {
      border: 1px solid #e8e8e8;
      border-radius: 12px;
      overflow: hidden;
      cursor: pointer;
      background: white;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
      transition: all 0.3s ease;
      display: block;
      width: 100%;

      &:hover {
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
        border-color: $primary-red;
        transform: translateY(-2px);
      }

      .service-image {
        width: 100%;
        height: auto;
        object-fit: cover;
      }

      .service-info {
        padding: 20px;

        .service-name {
          font-size: 17px;
          font-weight: 600;
          color: #2c3e50;
          margin-bottom: 10px;
          line-height: 1.3;
          display: -webkit-box;
          -webkit-line-clamp: 1;
          line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .service-desc {
          font-size: 14px;
          color: #666;
          line-height: 1.5;
          margin-bottom: 16px;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .service-price {
          font-size: 20px;
          font-weight: 700;
          color: $primary-red;
          margin-bottom: 8px;
        }

        .service-meta {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 14px;
          font-size: 13px;
          color: #999;

          .service-rating {
            color: #ff9900;
            font-weight: 600;
          }
        }

        .service-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;

          .service-tag {
            padding: 4px 10px;
            background: #f5f5f5;
            color: #666;
            border-radius: 12px;
            font-size: 12px;
            font-weight: 500;
          }

          .service-status-tag {
            padding: 4px 10px;
            border-radius: 12px;
            font-size: 12px;
            font-weight: 600;

            &.online {
              background: #e8f5e8;
              color: #2e7d2e;
            }

            &.offline {
              background: #fdeaea;
              color: #c53030;
            }
          }
        }
      }
    }
  }

  .no-services {
    text-align: center;
    padding: 60px;
    color: #999;
    font-size: 14px;
  }
}

/* 员工面板 */
.staff-panel {
  .staff-section {
    display: flex;
    gap: 32px;

    .staff-list {
      width: 320px;
      border-right: 1px solid #e8e8e8;
      padding-right: 32px;
      flex-shrink: 0;

      .staff-item {
        display: flex;
        align-items: center;
        gap: 16px;
        padding: 16px;
        border: 1px solid #e8e8e8;
        border-radius: 12px;
        margin-bottom: 16px;
        cursor: pointer;
        background: white;

        &:hover {
          border-color: $primary-red;
          box-shadow: 0 2px 8px rgba(231, 76, 60, 0.1);
        }

        &.selected {
          border-color: $primary-red;
          background: rgba(231, 76, 60, 0.04);
          box-shadow: 0 2px 8px rgba(231, 76, 60, 0.15);
        }

        .staff-avatar {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          object-fit: cover;
          flex-shrink: 0;
          border: 2px solid #f0f0f0;
        }

        .staff-basic {
          flex: 1;

          .staff-name {
            font-size: 15px;
            font-weight: 600;
            color: #2c3e50;
            margin-bottom: 8px;
          }

          .staff-tags {
            display: flex;
            gap: 8px;

            .staff-type-tag,
            .staff-sex-tag {
              padding: 3px 8px;
              background: #f5f5f5;
              color: #666;
              border-radius: 10px;
              font-size: 12px;
              font-weight: 500;
            }
          }
        }
      }
    }

    .staff-detail {
      flex: 1;

      .staff-detail-header {
        display: flex;
        gap: 20px;
        margin-bottom: 24px;
        align-items: flex-start;
        padding: 20px;
        background: #f9f9f9;
        border-radius: 12px;

        .staff-detail-avatar {
          width: 90px;
          height: 90px;
          border-radius: 50%;
          object-fit: cover;
          flex-shrink: 0;
          border: 3px solid white;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .staff-detail-info {
          flex: 1;

          h3 {
            font-size: 20px;
            font-weight: 700;
            color: #2c3e50;
            margin-bottom: 12px;
          }

          .staff-detail-tags {
            display: flex;
            gap: 10px;
            margin-bottom: 16px;

            .tag {
              padding: 6px 12px;
              background: white;
              color: #666;
              border: 1px solid #e0e0e0;
              border-radius: 16px;
              font-size: 13px;
              font-weight: 500;
            }
          }

          .chat-btn {
            padding: 10px 20px;
            background: $primary-red;
            color: white;
            border-radius: 8px;
            font-size: 15px;
            font-weight: 600;
            display: flex;
            align-items: center;
            gap: 6px;

            &:hover {
              background: $primary-red-dark;
            }
          }
        }
      }

      .staff-description {
        margin-bottom: 24px;
        padding: 20px;
        background: #fafafa;
        border-radius: 10px;

        h4 {
          font-size: 17px;
          font-weight: 600;
          color: #2c3e50;
          margin-bottom: 12px;
        }

        p {
          font-size: 15px;
          color: #555;
          line-height: 1.7;
        }
      }

      .staff-images {
        h4 {
          font-size: 17px;
          font-weight: 600;
          color: #2c3e50;
          margin-bottom: 16px;
        }

        .staff-image-grid {
          display: flex;
          flex-direction: column;
          gap: 12px;

          img {
            width: 100%;
            height: auto;
            object-fit: cover;
            border-radius: 10px;
            cursor: pointer;
            border: 1px solid #e8e8e8;
            display: block;
            transition: all 0.3s ease;

            &:hover {
              border-color: $primary-red;
              box-shadow: 0 4px 12px rgba(231, 76, 60, 0.2);
            }
          }
        }
      }
    }
  }

  .no-staff {
    text-align: center;
    padding: 60px;
    color: #999;
    font-size: 14px;
  }
}

/* 错误状态 */
.error-state {
  text-align: center;
  padding: 60px 16px;
  background: white;
  border-radius: 6px;
  color: #999;

  i {
    font-size: 48px;
    margin-bottom: 12px;
    display: block;
  }

  p {
    font-size: 16px;
    margin-bottom: 20px;
  }

  .error-btn {
    padding: 10px 20px;
    background: $primary-red;
    border: none;
    border-radius: 4px;
    color: white;
    cursor: pointer;
    transition: background 0.3s ease;

    &:hover {
      background: $primary-red-dark;
    }
  }
}

/* 动画效果 */
@keyframes loading {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

/* Element UI 组件样式保护 */
.merchant-detail-page {
  // 按钮样式保护
  ::v-deep .el-button {
    font-family: inherit !important;
    border-radius: 4px !important;
  }

  // 图标样式保护
  ::v-deep .el-icon {
    font-family: element-icons !important;
  }

  // 输入框样式保护
  ::v-deep .el-input {
    .el-input__inner {
      border-radius: 4px !important;
      border-color: #dcdfe6 !important;

      &:focus {
        border-color: $primary-red !important;
      }
    }
  }
}
</style>
